// External imports
import React from 'react';
import ReactDOM from 'react-dom';
import { ConnectedRouter } from 'connected-react-router';
import { BrowserRouter as Router } from 'react-router-dom';
import axios from 'axios';
// Internal imports
import App from '~/app/App.js';
import { NISTAuditLog } from '~/app/Utility/clientNISTLogger.js';
import { getPageName } from '~/app/Utility/general';
// Redux imports
import { Provider } from 'react-redux';
import { store, history, persistor } from '~/app/redux/index';
import { PersistGate } from 'redux-persist/integration/react';
import { NIST_LOG_EVENTS } from '~/app/Utility/clientNISTConstants.js';

// Optional: load jQuery globally if needed
const jqueryScript = document.createElement('script');
jqueryScript.src = 'https://ajax.googleapis.com/ajax/libs/jquery/3.3.1/jquery.min.js';
jqueryScript.id = 'jqueryScr';
document.head.appendChild(jqueryScript);

axios.interceptors.request.use(
  request => {
    const pathname = window.location.pathname;
    const pageName = getPageName(pathname);

    // include the user friendly page name in a custom header so we can access it in the API for NIST logs
    if (pageName) {
      request.headers['X-Page-Name'] = pageName;
    }

    return request;
  },
  error => Promise.reject(error),
);

// HANDLING this issue in `CLOIdleTimer` leaving this for a backup option
// Axios interceptor to handle expired tokens
// Add a response interceptor that ONLY handles rejected responses
axios.interceptors.response.use(
  response => response, // Pass successful responses through untouched
  error => {
    // Example: Redirect to login if unauthorized - only catch 400-500's - maybe make this more strict
    if (error?.response?.status >= 400) {
      // ordering is important, the LAST msg we should use is error.message
      let errorMsg = '';
      if (error?.message !== '' && error.response?.data?.error_message !== '') {
        errorMsg = `${error.message} - ${error.response.data.error_message}`;
      } else if (error.response?.data?.error_message !== '') {
        errorMsg = error.response.data.error_message;
      } else if (error?.message !== '') {
        errorMsg = error.message;
      }

      NISTAuditLog(
        NIST_LOG_EVENTS.ERROR_RESPONSE.key,
        errorMsg,
        error.response?.statusText,
        error.response.status,
        error?.code || '',
        true,
      );

      // Failed network requests do not have a status. The status is determined by the API and is included in the response.
      // All existing error codes are handled in NIST_LOG_EVENTS
    } else if (NIST_LOG_EVENTS[error?.code]) {
      NISTAuditLog(
        NIST_LOG_EVENTS[error.code].key,
        `${NIST_LOG_EVENTS[error.code].desc} Type: ${error?.name}`,
        error?.message,
        error.response?.status,
        error.code,
        true,
      );
    }

    // Always reject the error so individual requests can handle it if needed
    return Promise.reject(error);
  },
);

// --- React App Entry Point ---
ReactDOM.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <ConnectedRouter history={history}>
        <Router>
          <App />
        </Router>
      </ConnectedRouter>
    </PersistGate>
  </Provider>,
  document.getElementById('root'),
);
